import { Component, OnInit, Input, EventEmitter, Output, ViewChild, OnChanges, signal, NO_ERRORS_SCHEMA } from '@angular/core';
import { FlowObjectType, FlowObjectDefinition, FormSchema } from '../../../../models/flow-object.model';
import { ConditionalRecipient, ConfigSchema, ConfigSchemaSectionAccessLevel, ConfigSchemaSectionEDocsLocation } from '../../../../models/config-schema.model';
import { PapeisFilter } from '../default/fod-default.pipe';
import { AgentePublicoPapel } from '../../../../models/acesso-cidadao.model';
import { AcessoCidadaoService } from '../../../../services/acesso-cidadao.service';
import { Enums } from '../../../../shared/enums';
import { ToastrService } from 'ngx-toastr';
import { FlowDefinition } from '../../../../models/flow.model';
import { Utils } from '../../../../shared/utils';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { FOD_DISPATCH_PROCESS_TINYMCE_OPTIONS } from './tinymce-options';
import { IBaseOption } from '../../../../models/base.model';
import { AccessLevelComponent } from '../_edocs-fields/access-level/access-level.component';
import { EDocsLocationComponent } from '../_edocs-fields/edocs-location/edocs-location.component';
import { FlowObjectDefinitionService } from '../../../../services/flow-object-definition.service';
import { IMPORTS } from './_imports';

@Component({
    selector: 'fod-dispatch-process',
    templateUrl: './fod-dispatch-process.component.html',
    styleUrl: './fod-dispatch-process.component.scss',
    standalone: true,
    imports: IMPORTS,
    schemas: [NO_ERRORS_SCHEMA]
})
export class FlowObjectDetailsDispatchProcessComponent implements OnInit, OnChanges {
    // #region [ViewChild]
    @ViewChild('edocsLocationRef') edocsLocationRef: EDocsLocationComponent;
    @ViewChild('edocsLocationConditionalRef') edocsLocationConditionalRef: EDocsLocationComponent;
    @ViewChild('accessLevelRef') accessLevelRef: AccessLevelComponent;
    // #endregion

    // #region [Type props]
    FlowObjectType = FlowObjectType;
    RecipientMode = Enums.RecipientMode;
    Utils = Utils;
    // #endregion

    // #region [props]
    formFlowObjectSchemaFields: any[] = [];
    tinyMceOptionsProcess: any = FOD_DISPATCH_PROCESS_TINYMCE_OPTIONS;
    servidorNome = '';
    servidorPapelId = '';
    // #endregion

    // #region [signals]
    $configSchema = signal(null as ConfigSchema);
    $flowDefinition = signal(null as FlowDefinition);
    $isReadOnlyMode = signal(false);
    $recipientMode = signal(Enums.RecipientMode.Fixed);
    $configSchemaSectionEDocsLocation = signal(new ConfigSchemaSectionEDocsLocation());
    $configSchemaSectionEDocsLocationConditional = signal(new ConfigSchemaSectionEDocsLocation());
    $configSchemaSectionAccessLevel = signal(new ConfigSchemaSectionAccessLevel());
    $formFieldConditionalOptions = signal([] as IBaseOption[]);
    $formFieldConditional = signal(null as string);
    $valueConditional = signal(null as string);
    $isOperationEqualConditional = signal(true);
    $servidorCpf = signal('');
    $servidorPapelDisplay = signal('');
    $servidorPapelNome = signal(null as string);
    $servidorPapeis = signal([] as AgentePublicoPapel[]);
    $selectableServidorPapeis = signal([] as AgentePublicoPapel[]);
    $dispatchSummary = signal('');
    $isDispatchSummaryBootstrapFinished = signal(false);
    // #endregion

    // #region [Input/Output]
    @Input() _configSchema: ConfigSchema;
    @Input() _flowDefinition: FlowDefinition;
    @Input() _isReadOnlyMode: boolean;
    @Output() _configSchemaChangeEvent = new EventEmitter<ConfigSchema>();
    @Output() _recipientModeChange = new EventEmitter<Enums.RecipientMode>();
    // #endregion

    constructor(
        private toastr: ToastrService,
        private acessoCidadaoService: AcessoCidadaoService,
        private flowObjectDefinitionService: FlowObjectDefinitionService,
        private papeisFilter: PapeisFilter
    ) { }

    async ngOnInit() {
        let formFlowObject = this.$flowDefinition().flowObjectDefinitions.find(x => x.typeId == FlowObjectType.StartForm);
        if (formFlowObject != null) {
            if (Utils.isNullOrEmpty(formFlowObject.formSchema)) {
                const response = await this.flowObjectDefinitionService.getFormSchema(formFlowObject.id);

                if (!response.isSuccess) {
                    this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                    return;
                }

                formFlowObject.formSchema = response.data;
            }

            let formSchema = JSON.parse(formFlowObject.formSchema) as FormSchema;
            this.formFlowObjectSchemaFields = Utils.getFormFieldsWithUsableValue(formSchema.components);
        }

        for (let prop in this.$configSchemaSectionAccessLevel()) {
            Utils.updSignal(this.$configSchemaSectionAccessLevel, x => {
                x[prop] = this.$configSchema().taskDispatchProcess.accessLevel[prop] || x[prop];
                return x;
            });
        }

        for (let prop in this.$configSchemaSectionEDocsLocation()) {
            Utils.updSignal(this.$configSchemaSectionEDocsLocation, x => {
                x[prop] = this.$configSchema().taskDispatchProcess.dispatchRecipient[prop] || x[prop];
                return x;
            });
        }

        this.initDispatchInfo();

        this.tinyMceOptionsProcess.addDynamicMenu = editor => {
            editor.ui.registry.addMenuButton('vForm', {
                text: 'Formulário',
                fetch: callback => callback(this.getTinyMceMenuFormEntries(formFlowObject, editor))
            });
        };

        // debounce para que a opção de menu "Formulário" possa carregar corretamente
        setTimeout(() => this.$isDispatchSummaryBootstrapFinished.set(true), 100);
    }

    ngOnChanges() {
        this.$configSchema.set(this._configSchema);
        this.$flowDefinition.set(this._flowDefinition);
        this.$isReadOnlyMode.set(this._isReadOnlyMode);
    }

    clearDestinatario() {
        this.$configSchemaSectionEDocsLocation.set(new ConfigSchemaSectionEDocsLocation());

        Utils.updSignal(this.$configSchema, x => {
            Object.assign(x.taskDispatchProcess.dispatchRecipient, this.$configSchemaSectionEDocsLocation());
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    removeCondition(condition: ConditionalRecipient) {
        if (this.$isReadOnlyMode()) return;

        Utils.updSignal(this.$configSchema, x => {
            x.taskDispatchProcess.conditionalRecipients = x.taskDispatchProcess.conditionalRecipients.filter(x => x.timestamp != condition.timestamp);
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    moveCondition(condition: ConditionalRecipient, isDirectionUp = false) {
        if (this.$isReadOnlyMode()) return;

        let offset = isDirectionUp ? -1 : 1;

        let fromKey = condition.timestamp;
        let fromCondition = this.$configSchema().taskDispatchProcess.conditionalRecipients.find(x => x.timestamp == fromKey);
        let toCondition = this.$configSchema().taskDispatchProcess.conditionalRecipients[this.$configSchema().taskDispatchProcess.conditionalRecipients.indexOf(fromCondition) + offset];
        let toKey = toCondition.timestamp;

        fromCondition.timestamp = toKey;
        toCondition.timestamp = fromKey;

        Utils.updSignal(this.$configSchema, x => {
            x.taskDispatchProcess.conditionalRecipients = x.taskDispatchProcess.conditionalRecipients.sort((a, b) => a.timestamp.localeCompare(b.timestamp));
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    removeAllConditions() {
        if (this.$isReadOnlyMode()) return;

        Utils.updSignal(this.$configSchema, x => {
            x.taskDispatchProcess.conditionalRecipients = [];
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    prettyPrint(condition: ConditionalRecipient): string {
        return '<span>Se o valor de {0} for {1} {2}, despachar para {3}</span>'
            .replace('{0}', `<b style="color:red">${this.$formFieldConditionalOptions().find(x => x.value == condition.formFieldId).description}</b>`)
            .replace('{1}', `<b style="color:purple">${condition.isOperationEqual ? 'igual a' : 'diferente de'}</b>`)
            .replace('{2}', `<b style="color:blue">"${condition.value}"</b>`)
            .replace('{3}', `<b style="color:green">${condition.recipient.name}</b>`);
    }

    async searchServidorCpf() {
        if (Utils.isNotNullOrEmpty(this.$servidorCpf())) {
            let response = await this.acessoCidadaoService.getAgentePublicoSub(this.$servidorCpf());

            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            response = await this.acessoCidadaoService.getAgentePublico(response.data.sub);

            if (!response.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            this.servidorNome = response.data.nome;

            let responsePapeis = await this.acessoCidadaoService.getAgentePublicoPapeis(response.data.sub);

            if (!responsePapeis.isSuccess) {
                this.toastr.error(response.message.description, Enums.Messages.Error, Utils.getToastrErrorOptions());
                return;
            }

            this.$servidorPapeis.set(responsePapeis.data);
            this.$servidorPapeis.set(this.$servidorPapeis().toSorted((a, b) => {
                let first = a.nome || '';
                let second = b.nome || '';
                return first.localeCompare(second);
            }));
            Utils.setSignal(this.$selectableServidorPapeis, this.$servidorPapeis());
        }
    }

    clearServidorCpf() {
        this.$servidorCpf.set('');
        this.clearServidorPapel();
    }

    servidorPapelDisplayChange() {
        this.$selectableServidorPapeis.set(this.papeisFilter.transform(this.$servidorPapeis(), this.$servidorPapelDisplay()));
    }

    servidorPapelIdChange(event?: MatAutocompleteSelectedEvent) {
        if (event != null) {
            this.servidorPapelId = event.option.value.guid;
        }

        if (this.servidorPapelId == '') {
            this.clearServidorPapel();
        } else {
            let servidor = this.$servidorPapeis().find(x => x.guid == this.servidorPapelId);
            let papel = this.$servidorPapeis().find(x => x.guid == this.servidorPapelId);
            let suffix = `${papel.lotacao.nomeCurto} - ${papel.lotacao.organizacao.sigla} - ${papel.lotacao.organizacao.organizacaoPai.sigla}`;
            this.$servidorPapelNome.set(`${servidor.nome} - ${suffix}`.toUpperCase());
        }
    }

    displayServidorPapelId(option): string {
        return Utils.isNotNullOrEmpty(option)
            ? `${option.nome} - ${option.lotacao.nomeCurto} - ${option.lotacao.organizacao.sigla} - ${option.lotacao.organizacao.organizacaoPai.sigla}`.toUpperCase()
            : '';
    }

    clearServidorPapel() {
        this.servidorPapelId = '';
        this.$servidorPapelDisplay.set('');
        this.$servidorPapelNome.set(null);
        Utils.setSignal(this.$selectableServidorPapeis, this.$servidorPapeis());
    }

    selectServidorPapel() {
        Utils.updSignal(this.$configSchemaSectionEDocsLocation, x => {
            x.id = this.servidorPapelId;
            x.name = this.$servidorPapelNome();
            return x;
        });

        Utils.updSignal(this.$configSchema, x => {
            Object.assign(x.taskDispatchProcess.dispatchRecipient, this.$configSchemaSectionEDocsLocation());
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    resolveDispatchInfo(): boolean {
        if (this.$dispatchSummary()?.length < 5) {
            this.toastr.error(Enums.Messages.MandatoryDispatchSummary, Enums.Messages.Error, Utils.getToastrErrorOptions());
            return false;
        }

        let div = document.createElement('div');
        div.innerHTML = this.$dispatchSummary()?.replaceAll('<br />', '\n');
        Utils.updSignal(this.$configSchema, x => {
            x.taskDispatchProcess.dispatchSummary = div.textContent || div.innerText || '';
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());

        return true;
    }

    recipientModeChange(event?) {
        if (event != null) {
            this.$recipientMode.set(event);
        }

        if (this.$recipientMode() == Enums.RecipientMode.PublicAgent) {
            Utils.updSignal(this.$configSchema, x => {
                x.taskDispatchProcess.toPublicAgent = true;
                return x;
            });
        } else {
            Utils.updSignal(this.$configSchema, x => {
                x.taskDispatchProcess.toPublicAgent = false;
                return x;
            });
            this.clearServidorCpf();
        }

        if (this.$recipientMode() == Enums.RecipientMode.OwnerImmediateSupervisor) {
            Utils.updSignal(this.$configSchema, x => {
                x.taskDispatchProcess.toOwnerImmediateSupervisor = true;
                return x;
            });
        } else {
            Utils.updSignal(this.$configSchema, x => {
                x.taskDispatchProcess.toOwnerImmediateSupervisor = false;
                return x;
            });
        }

        if (this.$recipientMode() == Enums.RecipientMode.OwnerHeadOfOrganization) {
            Utils.updSignal(this.$configSchema, x => {
                x.taskDispatchProcess.toOwnerHeadOfOrganization = true;
                return x;
            });
        } else {
            Utils.updSignal(this.$configSchema, x => {
                x.taskDispatchProcess.toOwnerHeadOfOrganization = false;
                return x;
            });
        }

        if (this.$recipientMode() != Enums.RecipientMode.Fixed) {
            this.$configSchemaSectionEDocsLocation.set(new ConfigSchemaSectionEDocsLocation());

            Utils.updSignal(this.$configSchema, x => {
                Object.assign(x.taskDispatchProcess.dispatchRecipient, this.$configSchemaSectionEDocsLocation());
                return x;
            });

            this.edocsLocationRef?.clearPatriarca();
        }

        if (this.$recipientMode() != Enums.RecipientMode.Conditional) {
            this.$formFieldConditional.set(null);
            this.$valueConditional.set(null);

            Utils.updSignal(this.$configSchema, x => {
                x.taskDispatchProcess.conditionalRecipients = [];
                return x;
            });

            this.edocsLocationConditionalRef?.clearPatriarca();
        }

        this._configSchemaChangeEvent.emit(this.$configSchema());
        this._recipientModeChange.emit(this.$recipientMode());
    }

    updateSourceCode(forcedConfigSchema: ConfigSchema) {
        Utils.setSignal(this.$configSchema, forcedConfigSchema);

        Utils.setSignal(this.$configSchemaSectionEDocsLocation, Object.assign(this.$configSchemaSectionEDocsLocation(), this.$configSchema().taskDispatchProcess.dispatchRecipient));
        Utils.setSignal(this.$configSchemaSectionAccessLevel, Object.assign(this.$configSchemaSectionAccessLevel(), this.$configSchema().taskDispatchProcess.accessLevel));

        this.initDispatchInfo();
        this.accessLevelRef?.initLegalReasoningsInfo();

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    onUpdateEDocsLocation(event: ConfigSchemaSectionEDocsLocation) {
        Utils.setSignal(this.$configSchemaSectionEDocsLocation, Object.assign(this.$configSchemaSectionEDocsLocation(), event));
        Utils.updSignal(this.$configSchema, x => {
            Object.assign(x.taskDispatchProcess.dispatchRecipient, this.$configSchemaSectionEDocsLocation());
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    onUpdateEDocsLocationConditional(event: ConfigSchemaSectionEDocsLocation) {
        if (Utils.isNullOrEmpty(this.$formFieldConditional()) || Utils.isNullOrEmpty(this.$valueConditional())) {
            this.toastr.warning(Enums.Messages.MandatoryConditionalFields, Enums.Messages.Warning, Utils.getToastrErrorOptions());
            return;
        }

        Utils.setSignal(this.$configSchemaSectionEDocsLocationConditional, Object.assign(this.$configSchemaSectionEDocsLocationConditional(), event));

        Utils.updSignal(this.$configSchema, x => {
            x.taskDispatchProcess.conditionalRecipients.push(new ConditionalRecipient({
                formFieldId: this.$formFieldConditional(),
                value: this.$valueConditional(),
                isOperationEqual: this.$isOperationEqualConditional().toString().toLowerCase() == 'true',
                recipient: Utils.clone(this.$configSchemaSectionEDocsLocationConditional())
            }));
            return x;
        });

        if (this.$configSchema().taskDispatchProcess.conditionalRecipients.length == 1) {
            Utils.scrollToBottom('.scroll-wrapper');
        }

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    onUpdateAccessLevel(event: ConfigSchemaSectionAccessLevel) {
        Utils.setSignal(this.$configSchemaSectionAccessLevel, Object.assign(this.$configSchemaSectionAccessLevel(), event));
        Utils.updSignal(this.$configSchema, x => {
            Object.assign(x.taskDispatchProcess.accessLevel, this.$configSchemaSectionAccessLevel());
            return x;
        });

        this._configSchemaChangeEvent.emit(this.$configSchema());
    }

    // #region [===== private methods =====]
    // #endregion

    private initDispatchInfo() {
        this.$dispatchSummary.set(this.$configSchema().taskDispatchProcess.dispatchSummary?.replaceAll('\n', '<br />'));

        Utils.updSignal(this.$configSchema, x => {
            x.taskDispatchProcess.conditionalRecipients = x.taskDispatchProcess.conditionalRecipients || [];
            return x;
        });

        this.$formFieldConditionalOptions.set(this.formFlowObjectSchemaFields.map(x => ({ value: x.key, description: x.label })));

        this.$recipientMode.set(this.$configSchema()?.taskDispatchProcess.toPublicAgent == true
            ? Enums.RecipientMode.PublicAgent
            : this.$configSchema()?.taskDispatchProcess.toOwnerImmediateSupervisor == true
                ? Enums.RecipientMode.OwnerImmediateSupervisor
                : this.$configSchema()?.taskDispatchProcess.toOwnerHeadOfOrganization == true
                    ? Enums.RecipientMode.OwnerHeadOfOrganization
                    : this.$configSchema()?.taskDispatchProcess.conditionalRecipients.length > 0
                        ? Enums.RecipientMode.Conditional
                        : Enums.RecipientMode.Fixed
        );

        this.recipientModeChange();
    }

    private getTinyMceMenuFormEntries(flowObject: FlowObjectDefinition, editor): any[] {
        if (Utils.isNullOrEmpty(flowObject.formSchema)) return;

        let entries = [];
        let formSchema = JSON.parse(flowObject.formSchema) as FormSchema;

        Utils.getFormFieldsWithUsableValue(formSchema.components).forEach(item => {
            entries.push({
                type: 'menuitem',
                text: item.label,
                onAction: () => editor.insertContent(`{|${item.key}|}`)
            });
        });

        return entries;
    }
}
